import React, { useMemo, useState } from "react";
import CancelFlow from "../components/CancelFlow";

/* Dev-only (/__preview/cancel-flow): the cancel flow with sample data and a
   fake API, so every screen can be reviewed without a subscription.
   ?offer=0 hides the free month; ?fail=accept makes claiming fail;
   ?plan=pro turns off the Family → Pro switch; ?made=none|one|few. */
const U = "https://huzyagtfxwapsccsksib.supabase.co/storage/v1/object/public/users/";
const SAMPLE = {
  subscription: { store: "stripe", trialing: false, will_renew: true, period_end: "2026-10-30T19:00:00Z" },
  offer: { type: "free_month", days: 30 },
  support_email: "team@pixleyai.com",
  newest_original: {
    id: "9999f7ba-c613-44b5-8be6-90ff427cc4e4",
    name: "The Case Crew",
    image_url: "https://auth.pixleyai.com/storage/v1/object/public/videos/originals/covers/the-case-crew-cover.png",
  },
  made: {
    counts: { stories: 14, characters: 8, storybooks: 3 },
    stories: [
      ["Firefly Map Adventure", "4fd7355a-ee6d-4078-977a-f3ee38dd982f/stories/a92d8a31-84d1-4f0d-b066-6236ef5e6d84/cover.png"],
      ["Atlas and the Sleepy Stars", "4fd7355a-ee6d-4078-977a-f3ee38dd982f/stories/06142cce-d741-4162-bf29-5bd531ae59fb/cover.png"],
      ["Pran and Peanut's Spooky Surprise", "4fd7355a-ee6d-4078-977a-f3ee38dd982f/stories/495c9753-2894-4f93-9b29-6418ef384948/cover.png"],
      ["Luna and the Sneezing Unicorn", "4fd7355a-ee6d-4078-977a-f3ee38dd982f/stories/94516b88-50f0-4559-8764-4e2f8e70d72f/cover.png"],
      ["Atlas and Wool Try Rugby", "4fd7355a-ee6d-4078-977a-f3ee38dd982f/stories/95b2019b-753e-4a88-a03f-bccbddd79bff/cover.png"],
    ].map(([name, path], i) => ({ id: `s${i}`, name, image_url: U + path })),
    characters: [
      ["ig", "temp/character_preview/e83e46b9-3096-41cd-908c-85f2dfa9d113/62db4767-9ee6-4a1d-b55d-8adf22be51fe.png"],
      ["lynx", "temp/character_preview/e83e46b9-3096-41cd-908c-85f2dfa9d113/b1ca8bca-c2c1-4b22-8f2e-c7cadd429df5.png"],
      ["Sparkle", "temp/character_preview/4FD7355A-EE6D-4078-977A-F3EE38DD982F/f4ccad8a-f390-46c1-b439-931271f3a410.png"],
      ["Captain Kip", "temp/character_preview/4FD7355A-EE6D-4078-977A-F3EE38DD982F/4f779930-194a-4c0c-8618-4f4f7d8f05cd.png"],
      ["Pran", "temp/character_preview/4FD7355A-EE6D-4078-977A-F3EE38DD982F/1288e210-a0ba-4820-b708-e9ad0d76215f.png"],
      ["flam", "temp/character_preview/e83e46b9-3096-41cd-908c-85f2dfa9d113/6f3e1bda-acce-40d5-9e8d-b9187110b162.png"],
      ["Tim", "temp/character_preview/4FD7355A-EE6D-4078-977A-F3EE38DD982F/eecf2f2d-1e25-49ec-9250-f95dec64658e.png"],
    ].map(([name, path], i) => ({ id: `c${i}`, name, image_url: U + path })),
  },
};

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

export default function CancelFlowPreview() {
  const params = new URLSearchParams(window.location.search);
  const [open, setOpen] = useState(true);
  const client = useMemo(() => {
    const offer = params.get("offer") === "0" ? null : SAMPLE.offer;
    const downgrade = params.get("plan") === "pro" ? null
      : { type: "downgrade", to: "pro", price_display: "$14.99/month", from_price_display: "$24.99/month", saves: "$10 a month" };
    return {
      getCancelFlow: async () => {
        await wait(400);
        // ?made=none|one|few — how much the family has made (default: lots)
        const m = SAMPLE.made, amount = params.get("made");
        const made = amount === "none" ? { counts: { stories: 0, characters: 0, storybooks: 0 }, stories: [], characters: [] }
          : amount === "one" ? { counts: { stories: 1, characters: 0, storybooks: 0 }, stories: m.stories.slice(0, 1), characters: [] }
          : amount === "few" ? { counts: { stories: 2, characters: 2, storybooks: 0 }, stories: m.stories.slice(0, 2), characters: m.characters.slice(0, 2) }
          : m;
        return { ...SAMPLE, offer, downgrade, made };
      },
      startCancelFlow: async (reason) => {
        await wait(300);
        return { session_id: "preview", offer, downgrade: reason === "too_expensive" ? downgrade : null };
      },
      acceptCancelOffer: async () => {
        await wait(700);
        return params.get("fail") === "accept"
          ? { detail: "We couldn't apply your free month. Please try again." }
          : { status: "applied" };
      },
      confirmCancelFlow: async () => {
        await wait(700);
        return { status: "cancelled", period_end: "2026-10-30T19:00:00Z" };
      },
      acceptCancelDowngrade: async () => {
        await wait(700);
        return { status: "scheduled", starts_at: "2026-10-30T19:00:00Z" };
      },
      cancelFlowOutcome: async () => ({ status: "ok" }),
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div style={{ minHeight: "100vh", background: "#EAF3FF", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <button onClick={() => setOpen(true)} style={{ fontFamily: "'Fredoka', sans-serif", padding: "12px 20px", borderRadius: 999 }}>
        Open cancel flow
      </button>
      <CancelFlow isOpen={open} onClose={() => setOpen(false)} client={client} />
    </div>
  );
}
